// src/utils/statoNavigazione.js
// ═══════════════════════════════════════════════════════════════════════════
// NEXIORA RICORDA DOVE ERI (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Sul telefono, quando si passa a un'altra app (telefonata, WhatsApp, fotocamera), il
// sistema può chiudere la pagina per liberare memoria e, al ritorno, la ricarica: prima si
// ripartiva sempre dalla Home (o, per il titolare della piattaforma, dall'elenco aziende).
// Ora ogni spostamento (pagina, commessa, scheda della commessa, azienda aperta dal
// titolare della piattaforma) è annotato nella cronologia del browser:
//  • se la pagina viene ricaricata, si riapre esattamente lì;
//  • il tasto «indietro» del telefono torna alla schermata precedente invece di chiudere
//    l'app.
// Niente cambia nell'indirizzo visibile e niente viene scritto nel database: le note
// stanno nella cronologia di quella scheda del browser. Valgono solo per lo stesso utente
// (e la stessa azienda): se nella stessa scheda entra un altro utente, si riparte da capo.
// ═══════════════════════════════════════════════════════════════════════════

const VERSIONE = 1;
// Dati di navigazione che NON si ricordano: azioni una tantum (l'attività da aprire dal
// link di un'email si apre una volta, non a ogni ricarica).
const ESCLUSI = new Set(["taskId", "project"]);

function statoAttuale() {
  try { return window.history.state; } catch { return null; }
}

function scrivi(stato, nuovo) {
  try {
    if (nuovo) window.history.pushState(stato, "");
    else window.history.replaceState(stato, "");
  } catch { /* cronologia non disponibile: si continua come prima */ }
}

/** Lo stato annotato, se è di Nexiora e dello stesso utente; altrimenti null. */
export function statoValido(s, uid) {
  return s && s.nx === VERSIONE && uid && s.uid === uid ? s : null;
}

/** Lo stato annotato nella voce corrente della cronologia (vedi statoValido). */
export function statoSalvato(uid) {
  return statoValido(statoAttuale(), uid);
}

/** Da una navigazione (handleNavigate) tiene solo i dati semplici da ricordare. */
export function vocePerNavigazione(nav) {
  const voce = {};
  Object.keys(nav || {}).forEach(k => {
    const v = nav[k];
    if (ESCLUSI.has(k)) return;
    if (v === null || ["string", "number", "boolean"].includes(typeof v)) voce[k] = v;
  });
  if (!voce.projectId && nav?.project?.id) voce.projectId = nav.project.id;
  return voce;
}

/**
 * Annota una schermata. nuovo=true aggiunge un passo alla cronologia (il tasto «indietro»
 * ci torna), false sostituisce quello attuale. Se la schermata è la stessa di adesso non
 * aggiunge passi doppi.
 */
export function annota(uid, org, voce, nuovo = true) {
  if (!uid) return;
  const stato = { nx: VERSIONE, uid, org: org || null, ...voce };
  const prima = statoSalvato(uid);
  const uguale = prima && (prima.org || null) === stato.org && !!prima.owner === !!stato.owner &&
    prima.page === stato.page && (prima.projectId || null) === (stato.projectId || null);
  scrivi(stato, nuovo && !uguale);
}

/** Dentro una commessa: ricorda la scheda aperta (senza aggiungere passi). */
export function ricordaScheda(uid, projectId, scheda) {
  const s = statoSalvato(uid);
  if (!s || s.page !== "project" || s.projectId !== projectId || s.initialTab === scheda) return;
  scrivi({ ...s, initialTab: scheda }, false);
}

/** Toglie i parametri indicati dall'indirizzo lasciando intatta la nota di navigazione. */
export function togliParametri(chiavi) {
  try {
    const qs = new URLSearchParams(window.location.search);
    chiavi.forEach(k => qs.delete(k));
    const resto = qs.toString();
    window.history.replaceState(statoAttuale(), "", window.location.pathname + (resto ? `?${resto}` : "") + window.location.hash);
  } catch { /* indirizzo non modificabile */ }
}
